import { MagnifyingGlassIcon } from '@radix-ui/react-icons'
import { PhotoCrosshair } from 'features/kestrelViewer/ui/tools/photoreel/components/PhotoCrosshair'
import { usePhotoUrl } from 'features/kestrelViewer/ui/tools/photoreel/hooks/usePhotoUrl'
import React, { useState } from 'react'
import styled from 'styled-components'
import { KVPhoto } from 'types'

const THUMBNAIL_SIZE_PX = 128 // 8rem = 128px

const ThumbnailContainer = styled.div<{ $highlighted: boolean }>`
  width: ${THUMBNAIL_SIZE_PX / 16}rem;
  height: ${THUMBNAIL_SIZE_PX / 16}rem;
  min-width: ${THUMBNAIL_SIZE_PX / 16}rem;
  min-height: ${THUMBNAIL_SIZE_PX / 16}rem;
  background-color: ${({ theme }) => theme.forecast.background2};
  border-radius: 0.5rem;
  overflow: hidden;
  transition: transform 0.2s ease;
  position: relative;
  cursor: pointer;
  border: ${({ $highlighted, theme }) =>
    $highlighted ? `2px solid ${theme.forecast.primary}` : 'none'};

  &:hover {
    transform: scale(1.05);
    cursor: pointer;
  }
`

const ThumbnailImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`

const ErrorContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: red;
  font-size: 12px;
  text-align: center;
  padding: 8px;
`

const LoadingContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
`

interface PhotoReelThumbnailProps {
  photo: KVPhoto
  onClick: (id: string) => void
  highlighted?: boolean
  onPreviewClick?: (id: string) => void
}

const PreviewButton = styled.button`
  position: absolute;
  top: 6px;
  right: 6px;
  background: rgba(0, 0, 0, 0.6);
  color: white;
  border: none;
  border-radius: 4px;
  padding: 2px;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 2;
  opacity: 0.85;

  &:hover {
    opacity: 1;
    background: rgba(0, 0, 0, 0.75);
  }
`

export const PhotoReelThumbnail: React.FC<PhotoReelThumbnailProps> = ({
  photo,
  onClick,
  highlighted = false,
  onPreviewClick,
}) => {
  const [imageDimensions, setImageDimensions] = useState<{
    width: number
    height: number
  } | null>(null)

  const {
    url: thumbnailUrl,
    isLoading,
    error,
  } = usePhotoUrl(photo, 'thumbnail')

  const handleClick = (event: React.MouseEvent) => {
    event.preventDefault()
    event.stopPropagation()
    onClick(photo.id)
  }

  const handleImageLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
    const img = e.currentTarget
    setImageDimensions({
      width: img.naturalWidth,
      height: img.naturalHeight,
    })
  }

  const projection = photo.projection

  if (error) {
    return (
      <ThumbnailContainer onClick={handleClick} $highlighted={highlighted}>
        <ErrorContainer>Error loading image</ErrorContainer>
      </ThumbnailContainer>
    )
  }

  return (
    <ThumbnailContainer
      onClick={handleClick}
      onMouseDown={(e) => e.stopPropagation()}
      onMouseUp={(e) => e.stopPropagation()}
      $highlighted={highlighted}>
      {onPreviewClick && (
        <PreviewButton
          title='Open'
          onClick={(e) => {
            e.preventDefault()
            e.stopPropagation()
            onPreviewClick(photo.id)
          }}>
          <MagnifyingGlassIcon width={14} height={14} />
        </PreviewButton>
      )}
      {isLoading ? (
        <LoadingContainer>Loading...</LoadingContainer>
      ) : thumbnailUrl ? (
        <ThumbnailImage
          src={thumbnailUrl}
          alt='Photo thumbnail'
          onLoad={handleImageLoad}
        />
      ) : null}
      {photo.hasThumbnail && projection && imageDimensions && !isLoading && (
        <PhotoCrosshair
          x={projection.x}
          y={projection.y}
          scale={0.6}
          containerWidth={THUMBNAIL_SIZE_PX}
          containerHeight={THUMBNAIL_SIZE_PX}
          imageWidth={imageDimensions.width}
          imageHeight={imageDimensions.height}
          originalImageWidth={projection.originalImageWidth}
          originalImageHeight={projection.originalImageHeight}
        />
      )}
    </ThumbnailContainer>
  )
}
